import React, { useState, useEffect, useCallback } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import ClearIcon from "@mui/icons-material/Clear";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Typography,
  LinearProgress,
  TextField,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { useCustom } from "@refinedev/core";
import * as XLSX from "xlsx";

const DownloadRequests = () => {
  const [selected, setSelected] = useState("user");
  const [status, setStatus] = useState("all");
  const [investmentStartDate, setInvestmentStartDate] = useState("");
  const [investmentEndDate, setInvestmentEndDate] = useState("");
  const [allData, setAllData] = useState<any[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchProgress, setFetchProgress] = useState({ current: 0, total: 0 });
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const BATCH_SIZE = 30;

  const resetFetchState = useCallback(() => {
    setAllData([]);
    setCurrentOffset(0);
    setHasMore(true);
    setFetchProgress({ current: 0, total: 0 });
  }, []);

  useEffect(() => {
    resetFetchState();
  }, [
    selected,
    status,
    investmentStartDate,
    investmentEndDate,
    resetFetchState,
  ]);

  const investmentDateRangeInvalid =
    selected === "investment_requests" &&
    Boolean(
      investmentStartDate &&
        investmentEndDate &&
        investmentEndDate < investmentStartDate
    );

  // Keep status in sync with entity type so the dropdown always has a valid selection
  const statusOptionsByEntity: Record<
    string,
    { value: string; label: string }[]
  > = {
    user: [
      { value: "all", label: "All" },
      { value: "pending", label: "Pending" },
      { value: "accepted", label: "Approved" },
      { value: "rejected", label: "Rejected" },
    ],
    grant: [
      { value: "all", label: "All" },
      { value: "PROCESSED", label: "Processed" },
      { value: "REJECTED", label: "Rejected" },
    ],
    gv_requests: [
      { value: "all", label: "All" },
      { value: "PENDING", label: "Pending" },
      { value: "APPROVED", label: "Approved" },
      { value: "PROCESSED", label: "Processed" },
      { value: "REJECTED", label: "Rejected" },
    ],
    investment_requests: [
      { value: "all", label: "All" },
      { value: "pending", label: "Pending" },
      { value: "accepted", label: "Accepted" },
      { value: "rejected", label: "Rejected" },
    ],
    grantvestment: [
      { value: "all", label: "All" },
      { value: "DRAFT", label: "Draft" },
      { value: "RAISING", label: "Raising" },
      { value: "CLOSED", label: "Closed" },
      { value: "CANCELED", label: "Canceled" },
      { value: "EXITED", label: "Exited" },
      { value: "PROMOTING", label: "Promoting" },
      { value: "ARCHIVED", label: "Archived" },
    ],
  };
  const statusOptions = statusOptionsByEntity[selected] ?? statusOptionsByEntity.user;
  const validStatus = statusOptions.some((o) => o.value === status)
    ? status
    : (statusOptions[0]?.value ?? "all");

  useEffect(() => {
    setStatus("all");
  }, [selected]);

  const { refetch } = useCustom({
    url: `platform-admin/download-requests`,
    method: "get",
    config: {
      query: {
        status: status,
        requestType: selected,
        limit: BATCH_SIZE,
        offset: currentOffset,
        ...(selected === "investment_requests" && investmentStartDate
          ? { startDate: investmentStartDate }
          : {}),
        ...(selected === "investment_requests" && investmentEndDate
          ? { endDate: investmentEndDate }
          : {}),
      },
    },
    queryOptions: {
      enabled: false,
      onSuccess: (data: any) => {
        const newData = data?.data?.data || [];
        const pagination = data?.data?.pagination;

        setAllData((prev) => [...prev, ...newData]);
        setFetchProgress((prev) => ({
          current: prev.current + newData.length,
          total:
            prev.total +
            newData.length +
            (pagination?.hasNext ? BATCH_SIZE : 0),
        }));

        if (pagination?.hasNext) {
          setCurrentOffset(Number(pagination.nextOffset));
          setHasMore(true);
        } else {
          setHasMore(false);
          setIsFetching(false);
        }
      },
      onError: () => {
        setIsFetching(false);
        setHasMore(false);
      },
    },
  });

  // Auto-fetch next batch when offset changes and we're still fetching
  useEffect(() => {
    if (isFetching && hasMore && currentOffset >= 0) {
      refetch();
    }
  }, [currentOffset, isFetching, hasMore, refetch]);

  const startFetching = () => {
    if (!isFetching) {
      resetFetchState();
      setIsFetching(true);
      setCurrentOffset(0);
      // The useEffect will trigger the first fetch
      setTimeout(() => refetch(), 100);
    }
  };

  const handleChange = (event: { target: { value: string } }) => {
    const value = event.target.value;
    setSelected(value);
    setStatus(statusOptionsByEntity[value]?.[0]?.value ?? "all");
  };

  const handleStatusChange = (event: { target: { value: string } }) => {
    setStatus(event.target.value);
  };

  const handleDownload = () => {
    if (investmentDateRangeInvalid) {
      return;
    }
    if (!isFetching && allData.length > 0) {
      exportToExcel();
    } else if (allData.length === 0 && !isFetching) {
      // Start fetching if no data and not currently fetching
      startFetching();
    }
  };

  const formatDate = (d: any) => {
    if (!d) return "";
    const date = typeof d === "string" ? new Date(d) : d;
    return isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
  };

  const exportToExcel = () => {
    if (allData.length === 0) {
      alert("No data available to export.");
      return;
    }

    const now = new Date();
    const datePart = now.toISOString().split("T")[0];
    const localDatePart = `${now.getFullYear()}-${String(
      now.getMonth() + 1,
    ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const timePart = `${String(now.getHours()).padStart(2, "0")}${String(
      now.getMinutes(),
    ).padStart(2, "0")}${String(now.getSeconds()).padStart(2, "0")}`;

    if (selected === "grant") {
      const transformedData = allData.map((row: any) => {
        const info = row.information || {};
        const sched = row.scheduleInformation || {};
        const purpose = row.grantPurpose || {};
        const processDate =
          row.scheduledGrantRequests?.[0]?.executionDate ??
          sched.executionDate ??
          sched.lastExecutionDate ??
          sched.nextExecutionDate ??
          row.updatedAt;

        return {
          "Fund Name": row.fundAccount?.fundAccountName ?? "",
          "Fund Reference Number": row.fundAccount?.reference ?? "",
          "User Name": [row.user?.firstName, row.user?.lastName]
            .filter(Boolean)
            .join(" "),
          "Charity Ein": row.charity?.ein ?? "",
          "Charity Name": row.charity?.name ?? "",
          "Creation Date": formatDate(row.createdAt),
          "Process Date": formatDate(processDate),
          "Applied Amount": info.appliedAmount ?? "",
          "Grant Purpose Type": purpose.type ?? "",
          "Grant Purpose Note": purpose.note ?? "",
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(transformedData);
      const colWidths = Object.keys(transformedData[0] || {}).map((key) => ({
        wch: Math.max(key.length, 12),
      }));
      worksheet["!cols"] = colWidths;
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Grant Requests");
      const filename = `GrantRequests_${status.toLocaleLowerCase()}_${datePart}.xlsx`;
      XLSX.writeFile(workbook, filename);
      return;
    }

    if (selected === "gv_requests") {
      const transformedData = allData.map((row: any) => ({
        "Fund Name": row.fundAccount?.fundAccountName ?? "",
        "Fund Reference Number": row.fundAccount?.reference ?? "",
        "Requested By": [row.user?.firstName, row.user?.lastName]
          .filter(Boolean)
          .join(" "),
        "GrantVestment Name": row.grantVestment?.name ?? "",
        "Date Requested": formatDate(row.createdAt),
        "Date Accepted":
          row.status === "APPROVED" || row.status === "PROCESSED"
            ? formatDate(row.updatedAt)
            : "",
        "Amount Accepted": row.appliedAmount ?? "",
        Status: row.status ?? "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(transformedData);
      const colWidths = Object.keys(transformedData[0] || {}).map((key) => ({
        wch: Math.max(key.length, 16),
      }));
      worksheet["!cols"] = colWidths;
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "GV Requests");
      const filename = `GrantVestmentRequests_${status}_${datePart}_${timePart}.xlsx`;
      XLSX.writeFile(workbook, filename);
      return;
    }

    if (selected === "investment_requests") {
      const statusLabel = (s: string) => {
        if (s === "PENDING") return "Pending";
        if (s === "APPROVED") return "Accepted";
        if (s === "REJECTED") return "Rejected";
        return s;
      };
      const transformedData = allData.map((row: any) => ({
        "Fund Name": row.fundAccount?.fundAccountName ?? "",
        "Fund reference number": row.fundAccount?.reference ?? "",
        "Requested By": [row.user?.firstName, row.user?.lastName]
          .filter(Boolean)
          .join(" "),
        Ticker: row.ticker ?? "",
        "Type": row.type ?? "",
        "Requested Amount": row.requestedAmount ?? "",
        "Requested Units": row.requestedUnits ?? "",
        "Applied Amount": row.appliedAmount ?? "",
        "Applied Units": row.appliedUnits ?? "",
        "Creation date": formatDate(row.createdAt),
        "Execution date": formatDate(row.executionDate),
        Status: statusLabel(row.status ?? ""),
      }));

      const worksheet = XLSX.utils.json_to_sheet(transformedData);
      const colWidths = Object.keys(transformedData[0] || {}).map((key) => ({
        wch: Math.max(key.length, 18),
      }));
      worksheet["!cols"] = colWidths;
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Investment Requests");
      const statusForFile =
        validStatus === "all"
          ? "all"
          : String(validStatus).toLowerCase();
      const filename = `InvestmentRequests_${statusForFile}_${localDatePart}.xlsx`;
      XLSX.writeFile(workbook, filename);
      return;
    }

    if (selected === "grantvestment") {
      const transformedData = allData.map((row: any) => ({
        "GrantVestment Name": row.name ?? "",
        Deadline: formatDate(row.deadline),
        "Goal Amount": row.goalAmount ?? "",
        "All or Nothing": row.allOrNothing ? "Yes" : "No",
        "Display on Public Site": row.showGrantvestment ? "Yes" : "No",
        Category: row.category?.categoryName ?? "",
        Status: row.status ?? "",
        "Raised Amount": row.raisedAmount ?? "",
        "Number of Investors": row.grantVestorCounter ?? "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(transformedData);
      const colWidths = Object.keys(transformedData[0] || {}).map((key) => ({
        wch: Math.max(key.length, 16),
      }));
      worksheet["!cols"] = colWidths;
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "GrantVestments");

      const filename = `GrantVestments_${status.toLowerCase()}_${localDatePart}_${timePart}.xlsx`;
      XLSX.writeFile(workbook, filename);
      return;
    }

    const transformedData = allData.map((user: any) => {
      const fundNames =
        user.fundAccounts
          ?.map((fund: any) => fund.fund.fundAccountName)
          .join(", ") || "";

      const nameParts = [
        user.title,
        user.firstName,
        user.middleName,
        user.lastName,
        user.suffix,
      ].filter((part) => part && part.trim() !== "");

      const fullName = nameParts.join(" ");

      return {
        "Full Name": fullName,
        Sub: user.sub || "",
        Status: user.status,
        Email: user.email,
        "Date of Birth": user.dateOfBirth || "",
        "Address Line 1": user.addressLine1 || "",
        "Address Line 2": user.addressLine2 || "",
        City: user.city || "",
        State: user.state || "",
        Zip: user.zip || "",
        "Phone Number": user.phoneNumber || "",
        "Alternative Phone Number": user.alternativePhoneNumber || "",
        "Phone Number Is Mobile": user.phoneNumberIsMobile ? "Yes" : "No",
        "Alternative Phone Number Is Mobile":
          user.alternativePhoneNumberIsMobile ? "Yes" : "No",
        "Alternative Email": user.alternativeEmail || "",
        Reference: user.reference || "",
        "Fund Names": fundNames,
      };
    });

    // Create a worksheet from the transformed data
    const worksheet = XLSX.utils.json_to_sheet(transformedData);

    // Auto-size columns (optional)
    const colWidths = Object.keys(transformedData[0] || {}).map((key) => ({
      wch: Math.max(key.length, 15), // Minimum width of 15 characters
    }));
    worksheet["!cols"] = colWidths;

    // Create a workbook and append the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users Data");

    // Generate filename with timestamp and filters
    const filename = `${selected}_${status}_${datePart}.xlsx`;

    // Export the workbook as an Excel file
    XLSX.writeFile(workbook, filename);

    console.log(
      `Exported ${allData.length} ${selected} records with status: ${status}`
    );
  };

  const isDownloadReady = !isFetching && allData.length > 0;
  const showProgress = isFetching || allData.length > 0;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" alignItems="center" gap={2}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="download-selector-label">Select Option</InputLabel>
          <Select
            labelId="download-selector-label"
            value={selected}
            onChange={handleChange}
            label="Select Option"
            disabled={isFetching}
          >
            <MenuItem value="user">Users</MenuItem>
            <MenuItem value="gv_requests">
              GV Requests
            </MenuItem>
            <MenuItem value="grant">
              Grant Requests
            </MenuItem>
            <MenuItem value="transfer" disabled>
              Transfer
            </MenuItem>
            <MenuItem value="investment_requests">
              Investment Requests
            </MenuItem>
            <MenuItem value="grantvestment">GrantVestments</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="status-selector-label">Status</InputLabel>
          <Select
            key={selected}
            labelId="status-selector-label"
            value={validStatus}
            onChange={handleStatusChange}
            label="Status"
            disabled={isFetching}
          >
            {statusOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selected === "investment_requests" && (
          <>
            <TextField
              label="Start Date"
              type="date"
              value={investmentStartDate}
              onChange={(e) => setInvestmentStartDate(e.target.value)}
              disabled={isFetching}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 168 }}
              InputProps={{
                endAdornment: investmentStartDate ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Clear start date"
                      edge="end"
                      size="small"
                      onClick={() => setInvestmentStartDate("")}
                      disabled={isFetching}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
            <TextField
              label="End Date"
              type="date"
              value={investmentEndDate}
              onChange={(e) => setInvestmentEndDate(e.target.value)}
              disabled={isFetching}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 168 }}
              error={investmentDateRangeInvalid}
              helperText={
                investmentDateRangeInvalid
                  ? "End date cannot be earlier than start date"
                  : ""
              }
              InputProps={{
                endAdornment: investmentEndDate ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Clear end date"
                      edge="end"
                      size="small"
                      onClick={() => setInvestmentEndDate("")}
                      disabled={isFetching}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </>
        )}

        <Button
          variant="contained"
          color="primary"
          startIcon={
            isFetching ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <DownloadIcon />
            )
          }
          onClick={handleDownload}
          disabled={
            (isFetching && allData.length === 0) || investmentDateRangeInvalid
          }
        >
          {isFetching
            ? "Fetching..."
            : isDownloadReady
            ? `Download (${allData.length})`
            : "Fetch & Download"}
        </Button>
      </Box>

      {showProgress && (
        <Box sx={{ width: "100%" }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="body2" color="textSecondary">
              {isFetching
                ? `Fetching records... ${fetchProgress.current} collected`
                : `Ready to download ${allData.length} records`}
            </Typography>
            {isFetching && (
              <Typography variant="body2" color="textSecondary">
                {Math.round(
                  (fetchProgress.current / Math.max(fetchProgress.total, 1)) *
                    100
                )}
                %
              </Typography>
            )}
          </Box>
          {isFetching && (
            <LinearProgress
              variant={
                fetchProgress.total > 0 ? "determinate" : "indeterminate"
              }
              value={
                fetchProgress.total > 0
                  ? (fetchProgress.current / fetchProgress.total) * 100
                  : 0
              }
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default DownloadRequests;
